import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Volume2, ChevronDown, Calculator } from "lucide-react";
import MaroonNavbar from "@/components/new/MaroonNavbar";
import MaroonFooter from "@/components/new/MaroonFooter";
import CheckoutDrawer from "@/components/new/CheckoutDrawer";
import WhatsAppButton from "@/components/new/WhatsAppButton";
import HoverCycleImage from "@/components/new/HoverCycleImage";

const C = { maroon: "#621B21", maroonDark: "#5C0F1C", yellow: "#F5C800", beige: "#F5EDD6", white: "#FFFFFF" };
const VIDEO_URL = "https://customer-assets.emergentagent.com/job_2e8a6a93-30a9-4e91-a7a5-a9394acf49bd/artifacts/rw86pha9_Ethia%20in%20action%20video.mp4";

const TRIAL_NEW = "https://customer-assets-rejwkqb3.emergentagent.net/job_2e8a6a93-30a9-4e91-a7a5-a9394acf49bd/artifacts/whgfkde7_Trial%20Pack%20Ethia%20%281%29.png";
const VALUE_NEW = "https://customer-assets-rejwkqb3.emergentagent.net/job_2e8a6a93-30a9-4e91-a7a5-a9394acf49bd/artifacts/lp2gvf2w_Ethia%20Value%20Pack%20%281%29.png";
const FAMILY_NEW = "https://customer-assets-rejwkqb3.emergentagent.net/job_2e8a6a93-30a9-4e91-a7a5-a9394acf49bd/artifacts/qcz1ezz2_Ethia%20Family%20Pack%20%281%29.png";
const BACK_IMG = "https://customer-assets-rejwkqb3.emergentagent.net/job_2e8a6a93-30a9-4e91-a7a5-a9394acf49bd/artifacts/kuzbrmjm_Ethia%20Back%20of%20the%20Pack%20Image.webp";
const TRIAL_ACTION = "https://customer-assets-rejwkqb3.emergentagent.net/job_2e8a6a93-30a9-4e91-a7a5-a9394acf49bd/artifacts/cmpv1zf0_Ethia%20Trial%20Pack%20Image%203.png";
const VALUE_ACTION = "https://customer-assets-rejwkqb3.emergentagent.net/job_2e8a6a93-30a9-4e91-a7a5-a9394acf49bd/artifacts/g0tiuobp_Ethia%20Value%20Pack%20Image%202.png";
const FAMILY_ACTION = "https://customer-assets-rejwkqb3.emergentagent.net/job_2e8a6a93-30a9-4e91-a7a5-a9394acf49bd/artifacts/ooeh3ljq_Ethia%20Family%20Pack%203.png";

const PUPPY_PHOTO = "https://images.unsplash.com/photo-1526660690293-bcd32dc3b123?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwyfHxwdXBweSUyMHNtYWxsJTIwZG9nJTIwcG9ydHJhaXR8ZW58MHx8fHwxNzkwMzQyMDAxfDA&ixlib=rb-4.1.0&q=85";
const POM_PHOTO = "https://images.pexels.com/photos/9649679/pexels-photo-9649679.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const BEAGLE_PHOTO = "https://images.unsplash.com/photo-1543466835-00a7907e9de1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwxfHxiZWFnbGUlMjBkb2clMjBwb3J0cmFpdHxlbnwwfHx8fDE3OTAzNDIwMDF8MA&ixlib=rb-4.1.0&q=85";
const GSD_PHOTO = "https://images.unsplash.com/photo-1649571069618-99a265749d5b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwxfHxnZXJtYW4lMjBzaGVwaGVyZCUyMGRvZyUyMHBvcnRyYWl0fGVufDB8fHx8MTc5MDM0MjAwMXww&ixlib=rb-4.1.0&q=85";

const BOWL_IMG = "https://customer-assets.emergentagent.com/job_2e8a6a93-30a9-4e91-a7a5-a9394acf49bd/artifacts/dv899zqk_bowl%20image.png";

const useReveal = () => {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => { const el = ref.current; if (!el) return; const o = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVis(true); }, { threshold: 0.2 }); o.observe(el); return () => o.disconnect(); }, []);
  return [ref, vis];
};

const LARGE_NEW = "https://customer-assets-rejwkqb3.emergentagent.net/job_2e8a6a93-30a9-4e91-a7a5-a9394acf49bd/artifacts/4ed9b9118f0f671d_Large%20Pack%201%20pouch%20of%20300g%20Price.png";

const PRICING_PRODUCTS = [
  { id: "trial", slug: "trial-pack", name: "Trial Pack", desc: "2 Pouches of 80g each · 160g in total", price: 199, gstPrice: 235, image: TRIAL_NEW, images: [TRIAL_NEW, BACK_IMG, TRIAL_ACTION] },
  { id: "value", slug: "value-pack", name: "Value Pack", desc: "5 Pouches of 80g each · 400g in total", price: 499, gstPrice: 589, image: VALUE_NEW, images: [VALUE_NEW, BACK_IMG, VALUE_ACTION] },
  { id: "large", slug: "large-pack", name: "Large Pack", desc: "1 Pouch of 300g", price: 237, gstPrice: 280, image: LARGE_NEW, images: [LARGE_NEW, BACK_IMG, LARGE_NEW] },
  { id: "family", slug: "family-pack", name: "Family Pack", desc: "12 Pouches of 80g each · 960g in total", price: 999, gstPrice: 1179, image: FAMILY_NEW, images: [FAMILY_NEW, BACK_IMG, FAMILY_ACTION], popular: true },
];

/* ─── Shop All — Premium Pricing Cards with Hover Image Cycle ─── */
const ShopAllSection = ({ onOrder }) => (
  <section className="pt-24 md:pt-28" data-testid="shop-all-section">
    <div className="py-10 md:py-14" style={{ background: C.beige }}>
      <div className="max-w-[1200px] mx-auto px-5 text-center">
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-2" style={{ color: C.maroon, fontFamily: "Fraunces, serif" }} data-testid="shop-heading">Choose Your Ethia Pack</h1>
        <p className="text-sm" style={{ color: `${C.maroon}99` }}>Thoughtfully formulated nutrition for your dog.</p>
      </div>
    </div>
    <div className="py-12 md:py-20" style={{ background: "#4a0e0e" }}>
      <div className="max-w-[1400px] mx-auto px-5 md:px-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {PRICING_PRODUCTS.map((p) => (
            <div key={p.id} className={`rounded-2xl overflow-hidden shadow-xl transition-all duration-300 hover:-translate-y-2 ${p.popular ? "ring-2" : ""}`} style={{ background: "#f2ede7", ringColor: p.popular ? "#e6b93d" : "transparent" }} data-testid={`product-card-${p.id}`}>
              {p.popular && <div className="text-center py-2.5 text-sm font-bold" style={{ background: "#e6b93d", color: "#4a0e0e" }}>Most Popular</div>}
              <div className="p-3">
                <div className="rounded-xl overflow-hidden" style={{ aspectRatio: "4/5", background: "#f8f5f0" }}>
                  <HoverCycleImage images={p.images} slug={p.slug} alt={p.name} className="w-full h-full object-contain rounded-xl" data-testid={`product-img-${p.id}`} />
                </div>
              </div>
              <div className="px-5 pb-5 text-center">
                <Link to={`/products/${p.slug}`} className="hover:underline">
                  <h3 className="text-lg md:text-xl font-bold mb-1" style={{ color: "#4a0e0e", fontFamily: "Fraunces, serif" }}>{p.name}</h3>
                </Link>
                <p className="text-xs mb-4" style={{ color: "#4a0e0e", opacity: 0.7 }}>{p.desc}</p>
                <div className="mb-4">
                  <div className="text-3xl font-black" style={{ color: "#4a0e0e", fontFamily: "Fraunces, serif" }}>₹{p.price}</div>
                  <div className="text-xs mt-1" style={{ color: "#4a0e0e", opacity: 0.5 }}>₹{p.gstPrice.toLocaleString()} (incl. GST)</div>
                </div>
                <button onClick={() => onOrder(p.id)} className="w-full font-bold py-3.5 rounded-full text-sm transition-all hover:scale-[1.02]" style={{ background: "#e6b93d", color: "#4a0e0e" }} data-testid={`order-btn-${p.id}`}>
                  Order Now <ArrowRight className="inline ml-1" size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

/* ─── Subscription Details ─── */
const SubscriptionSection = () => {
  const tiers = [
    { label: "A", discount: "10%", color: C.beige, minOrder: "₹3,000", commitment: "One-time", freeMonth: null, desc: "Purchase from the website with a minimum basket value of ₹3,000 to avail the discount." },
    { label: "B", discount: "20%", color: C.yellow, minOrder: "₹3,000 or 3 Family Packs", commitment: "4 months", freeMonth: "5th month free", desc: "Customized subscription with a 4-month commitment. Your 5th month's supply is on us, based on the pack size chosen during subscription." },
    { label: "C", discount: "Up to 30%", color: C.yellow, minOrder: "₹3,000 or 3 Family Packs", commitment: "5 months", freeMonth: "6th & 7th month free", desc: "Customized subscription with a minimum 5-month commitment. Your 6th and 7th month's supply is on us, based on the pack size chosen." },
  ];
  return (
    <section className="py-14 md:py-20" style={{ background: C.maroon }} data-testid="subscription-section">
      <div className="max-w-[1100px] mx-auto px-5 md:px-[52px]">
        <div className="text-center mb-4"><span className="text-xs font-bold tracking-[0.2em] uppercase" style={{ color: C.yellow }}>SAVE MORE</span></div>
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-3" style={{ color: C.white, fontFamily: "Fraunces, serif" }}>Subscription Plans</h2>
        <p className="text-center text-sm mb-10" style={{ color: `${C.beige}CC` }}>The longer you commit, the more you save.</p>
        <div className="rounded-2xl p-6 md:p-8 mb-6 relative overflow-hidden" style={{ background: C.maroonDark, border: `2px solid ${C.yellow}` }} data-testid="sub-annual">
          <div className="absolute top-0 right-0 px-4 py-1 rounded-bl-xl text-xs font-bold" style={{ background: C.yellow, color: C.maroon }}>Best Value</div>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h3 className="text-xl font-bold mb-1" style={{ color: C.yellow, fontFamily: "Fraunces, serif" }}>Annual Plan — Highest Savings</h3>
              <p className="text-sm" style={{ color: `${C.beige}CC` }}>Pay for 8 months, get 12 months of supply. 4 months complimentary based on your chosen pack size.</p>
            </div>
            <div className="text-center md:text-right flex-shrink-0">
              <div className="text-3xl font-black" style={{ color: C.yellow, fontFamily: "Fraunces, serif" }}>4 Months Free</div>
              <div className="text-xs mt-1" style={{ color: `${C.beige}99` }}>12 months commitment</div>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {tiers.map((t, i) => (
            <div key={i} className="rounded-2xl p-5 md:p-6" style={{ background: C.maroonDark }} data-testid={`sub-tier-${t.label}`}>
              <div className="flex items-center gap-3 mb-3">
                <span className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold" style={{ background: C.yellow, color: C.maroon }}>{t.label}</span>
                <span className="text-2xl font-black" style={{ color: C.white, fontFamily: "Fraunces, serif" }}>{t.discount}</span>
              </div>
              <div className="space-y-2 text-xs mb-4" style={{ color: `${C.beige}CC` }}>
                <div className="flex justify-between"><span style={{ color: `${C.beige}80` }}>Min. Order</span><span className="font-semibold" style={{ color: C.beige }}>{t.minOrder}</span></div>
                <div className="flex justify-between"><span style={{ color: `${C.beige}80` }}>Commitment</span><span className="font-semibold" style={{ color: C.beige }}>{t.commitment}</span></div>
                {t.freeMonth && <div className="flex justify-between"><span style={{ color: `${C.beige}80` }}>Benefit</span><span className="font-bold" style={{ color: C.yellow }}>{t.freeMonth}</span></div>}
              </div>
              <p className="text-[11px] leading-relaxed" style={{ color: `${C.beige}99` }}>{t.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─── Feeding Pattern Section (moved from Science, restyled beige/white) ─── */
const FeedingPatternSection = () => {
  const [showCalc, setShowCalc] = useState(false);
  const [ref, vis] = useReveal();
  const dogs = [
    { size: "Puppies", weight: "1–5 kg", pouches: "1½–5½", photo: PUPPY_PHOTO },
    { size: "Small", weight: "5–10 kg", pouches: "5½–9", photo: POM_PHOTO },
    { size: "Medium", weight: "10–25 kg", pouches: "9–18", photo: BEAGLE_PHOTO },
    { size: "Large", weight: "25–40 kg", pouches: "18–26", photo: GSD_PHOTO },
  ];
  return (
    <section ref={ref} className="py-14 md:py-20" style={{ background: C.beige }} data-testid="feeding-pattern">
      <div className={`max-w-[900px] mx-auto px-5 md:px-[52px] transition-all duration-700 ${vis ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-2" style={{ color: C.maroon, fontFamily: "Fraunces, serif" }}>How Much to Feed Your Dog</h2>
        <p className="text-center text-xs mb-8" style={{ color: `${C.maroon}80` }}>Based on an 80g pouch &middot; Energy density 86 kcal/100g</p>

        <div className="space-y-3 mb-6">
          {dogs.map((d, i) => (
            <div key={i} className="rounded-xl p-4 md:p-5 flex items-center justify-between shadow-sm" style={{ background: C.white }} data-testid={`feed-card-${i}`}>
              <div className="flex items-center gap-3">
                <img src={d.photo} alt={d.size} className="w-10 h-10 md:w-12 md:h-12 rounded-full object-cover flex-shrink-0" style={{ border: `2px solid ${C.yellow}` }} />
                <div>
                  <div className="font-bold text-sm" style={{ color: C.maroon }}>{d.size}</div>
                  <div className="text-xs" style={{ color: `${C.maroon}80` }}>{d.weight}</div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl md:text-3xl font-black" style={{ color: C.maroon, fontFamily: "Fraunces, serif" }}>{d.pouches}</div>
                <div className="text-[10px]" style={{ color: `${C.maroon}80` }}>pouches/day</div>
              </div>
            </div>
          ))}
        </div>

        <p className="text-xs text-center mb-8" style={{ color: `${C.maroon}80` }}>Adjust according to your dog's activity level.</p>

        {/* Expandable calculation */}
        <div className="rounded-xl overflow-hidden" style={{ background: C.white, border: `1px solid ${C.maroon}10` }}>
          <button onClick={() => setShowCalc(!showCalc)} className="w-full flex items-center justify-between px-5 py-3.5 text-left" data-testid="calc-toggle">
            <span className="text-xs font-bold uppercase tracking-wider flex items-center gap-2" style={{ color: C.maroon }}>
              <Calculator size={14} /> How We Calculate This
            </span>
            <ChevronDown size={16} className={`transition-transform duration-300 ${showCalc ? "rotate-180" : ""}`} style={{ color: C.maroon }} />
          </button>
          {showCalc && (
            <div className="px-5 pb-4 space-y-1 text-xs font-mono" style={{ color: `${C.maroon}CC` }}>
              <p>RER = 70 × body weight<sup>0.75</sup></p>
              <p>DER = 1.6 × RER</p>
              <p>Daily grams = DER ÷ 0.86</p>
              <p>Pouches/day = daily grams ÷ 80</p>
              <p className="text-[10px] mt-3 italic font-sans" style={{ color: `${C.maroon}60` }}>This chart reflects an average adult, neutered dog at maintenance. Intact adults, highly active dogs, or dogs prone to weight gain may need adjustment.</p>
            </div>
          )}
        </div>

        {/* Transition Guide */}
        <div className="mt-14">
          <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-center mb-3" style={{ color: C.maroon, fontFamily: "Fraunces, serif" }}>How to Start Feeding with ETHIA</h3>
          <p className="text-center text-xs mb-8 max-w-xl mx-auto" style={{ color: `${C.maroon}99` }}>Introduce ETHIA gradually over 7 days by mixing it with your dog's current food:</p>
          <p className="text-xs text-center font-bold mb-8 max-w-xl mx-auto leading-relaxed" style={{ color: C.maroon }}>For best results, mix ETHIA with your dog's regular meal (rice or an equivalent) rather than feeding it alone. The number of pouches per day is adjusted based on how much of your dog's daily energy ETHIA provides.</p>
          <div className="grid grid-cols-4 gap-2 md:gap-4 mb-6">
            {[["Day 1","25%","75%"],["Day 3","50%","50%"],["Day 5","75%","25%"],["Day 7","100%","0%"]].map(([day,ethia,curr],i) => (
              <div key={i} className="rounded-xl p-3 md:p-4 text-center shadow-sm" style={{ background: C.white }}>
                <div className="text-xs font-bold mb-2" style={{ color: C.maroon }}>{day}</div>
                <div className="text-sm font-bold mb-0.5" style={{ color: C.maroon }}>{ethia} ETHIA</div>
                {curr !== "0%" ? <div className="text-[10px]" style={{ color: `${C.maroon}80` }}>{curr} current food</div> : <div className="text-[10px] font-bold" style={{ color: C.yellow }}>Full ETHIA!</div>}
              </div>
            ))}
          </div>
          <p className="text-xs text-center italic" style={{ color: "#E57373" }}>If loose stools or refusal to eat occurs, slow the transition down and consult your veterinarian if symptoms persist.</p>
        </div>
      </div>
    </section>
  );
};

/* ─── What Goes In The Bowl ─── */
const INGREDIENT_MAP = [
  { name: "Cultured Chicken Cells", benefit: "Protein" },
  { name: "Pea Protein", benefit: "Plant Protein" },
  { name: "Shiitake Mushroom Extract", benefit: "Umami Flavour" },
  { name: "Chickpea Flour", benefit: "Fiber Source" },
  { name: "Pumpkin Powder", benefit: "Pumpkin Fiber" },
  { name: "Potato Starch", benefit: "Carb Source" },
  { name: "Yeast Extract", benefit: "Umami Flavour" },
  { name: "Sodium Alginate", benefit: "Binding Agent" },
  { name: "Vitamins and Minerals", benefit: "Essential Nutrients" },
];

const IngredientsSection = () => {
  const [activeIdx, setActiveIdx] = useState(null);
  const [ref, vis] = useReveal();
  const toggle = (i) => setActiveIdx(activeIdx === i ? null : i);
  return (
    <section ref={ref} className="py-14 md:py-20" style={{ background: C.beige }} data-testid="products-ingredients-section">
      <div className="max-w-[1100px] mx-auto px-5 md:px-[52px]">
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-2" style={{ color: C.maroon, fontFamily: "Fraunces, serif" }}>What Goes In The Bowl</h2>
        <p className="text-center text-sm md:text-base mb-10" style={{ color: `${C.maroon}99` }}>Every ingredient has a name you know and a reason to be there.</p>
        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-center transition-all duration-700 ${vis ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="space-y-2">
            {INGREDIENT_MAP.map((ing, i) => (
              <div key={i} className="relative flex items-center gap-3 py-2.5 px-3 rounded-lg hover:bg-white/60 transition-colors" onMouseEnter={() => setActiveIdx(i)} onMouseLeave={() => setActiveIdx(null)} onClick={() => toggle(i)} data-testid={`ingredient-${i}`}>
                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: C.yellow }} />
                <span className="text-sm font-bold" style={{ color: C.maroon, fontFamily: "Manrope, sans-serif" }}>{ing.name}</span>
                {activeIdx === i && (
                  <span className="ml-2 px-3 py-1 rounded-full text-[10px] font-bold animate-[fadeIn_0.2s_ease-out]" style={{ background: C.yellow, color: C.maroon }}>{ing.benefit}</span>
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-center">
            <img src={BOWL_IMG} alt="ETHIA Bowl" className="w-full max-w-sm rounded-2xl" style={{ boxShadow: "0 8px 32px rgba(98,27,33,0.1)" }} />
          </div>
        </div>
      </div>
    </section>
  );
};

/* ─── Video Section ─── */
const VideoSection = () => {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const toggleMute = () => { if (videoRef.current) { videoRef.current.muted = !muted; setMuted(!muted); } };
  return (
    <section className="py-16 md:py-24" style={{ background: C.maroonDark }} data-testid="products-video-section">
      <div className="max-w-[1000px] mx-auto px-6 md:px-[52px]">
        <div className="text-center mb-4"><span className="text-xs font-bold tracking-[0.2em] uppercase" style={{ color: C.yellow }}>See ETHIA in Action</span></div>
        <div className="relative rounded-2xl md:rounded-3xl overflow-hidden shadow-2xl">
          <video ref={videoRef} src={VIDEO_URL} autoPlay muted loop playsInline className="w-full aspect-video bg-black" data-testid="products-video-player" />
          <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 to-transparent">
            <p className="text-sm md:text-base" style={{ color: C.white }}>Every day, millions of dogs are fed food that most of us would never look twice at.</p>
          </div>
          <button onClick={toggleMute} className="absolute bottom-4 right-4 w-10 h-10 rounded-full flex items-center justify-center" style={{ background: C.yellow }}>
            <Volume2 size={18} style={{ color: C.maroon }} />
          </button>
        </div>
        <p className="text-center text-xs mt-4" style={{ color: `${C.beige}99` }}>Understanding Cultivated Pet Food — Learn how ETHIA is revolutionising pet nutrition in India.</p>
      </div>
    </section>
  );
};

/* ─── Final CTA ─── */
const FinalCTA = ({ onOrder }) => (
  <section className="py-16 md:py-20" style={{ background: C.beige }} data-testid="products-final-cta">
    <div className="max-w-[800px] mx-auto px-6">
      <div className="rounded-2xl md:rounded-3xl p-8 md:p-14 text-center" style={{ background: C.maroonDark }}>
        <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3" style={{ color: C.white, fontFamily: "Fraunces, serif" }}>Be First. Feed Better. Save Lives.</h3>
        <p className="text-sm md:text-base mb-8" style={{ color: `${C.beige}CC` }}>Be the first in India to feed your dog food that saves lives.</p>
        <button onClick={() => onOrder(null)} className="font-bold px-10 py-4 rounded-full transition-all hover:scale-105 text-sm md:text-base" style={{ background: C.yellow, color: C.maroon }} data-testid="products-cta-btn">Order Now <ArrowRight className="inline ml-1" size={18} /></button>
      </div>
    </div>
  </section>
);

/* ─── Page ─── */
const ProductsPage = () => {
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [selectedPkg, setSelectedPkg] = useState(null);

  useEffect(() => { document.title = "ETHIA | Products"; }, []);

  const handleOrder = (sku) => {
    setSelectedPkg(sku);
    setIsCheckoutOpen(true);
    if (typeof window.fbq === "function") window.fbq("track", "InitiateCheckout");
  };

  return (
    <div className="min-h-screen" style={{ fontFamily: "Manrope, sans-serif" }} data-testid="products-page">
      <MaroonNavbar onOrder={() => handleOrder(null)} />
      <ShopAllSection onOrder={handleOrder} />
      <SubscriptionSection />
      <FeedingPatternSection />
      <IngredientsSection />
      <VideoSection />
      <FinalCTA onOrder={handleOrder} />
      <MaroonFooter />
      <WhatsAppButton />
      <CheckoutDrawer isOpen={isCheckoutOpen} onClose={() => setIsCheckoutOpen(false)} preselectedSku={selectedPkg} />
    </div>
  );
};

export default ProductsPage;
